import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/utils/cn";
import { ModalShell } from "../ModalShell";
import { ModalLoadingSkeleton } from "./ModalLoadingSkeleton";
import { ModalErrorPanel } from "./ModalErrorPanel";
import { useSubstanceInventory } from "@/hooks/useSubstanceInventory";
import { useSubstanceMarketProducts } from "@/hooks/useSubstanceMarketProducts";
import { parseSubstanceKey } from "@/utils/types/substance";
import { lookupServerCompound } from "@/utils/serverCompounds";

type SupplierModalProps = { compoundId: string; onClose: () => void };

type Scope = "noahs_farm" | "own_org" | "own_profile";

type FlatRow = {
  key:           string;
  src:           "INV" | "SPP";
  catalog:       string;
  supplier:      string;
  scope?:        Scope;
  size:          string;
  price:         number;
  currency:      string;
  pricePerGram:  number | null;
  updated:       string;
  isCheapest:    boolean;
};

function scopeChipLabel(scope: Scope | undefined): string | null {
  if (scope === "noahs_farm")  return "Noah's Farm";
  if (scope === "own_profile") return "Yours";
  if (scope === "own_org")     return "Org";
  return null;
}

export function SupplierModal({ compoundId, onClose }: SupplierModalProps) {
  const qc  = useQueryClient();
  const sub = parseSubstanceKey(compoundId);
  const mp  = useSubstanceMarketProducts(sub?.kind ?? null, sub?.id ?? null);
  const inv = useSubstanceInventory(sub?.kind ?? null, sub?.id ?? null);
  const sc  = lookupServerCompound(compoundId);

  const rows = useMemo<FlatRow[]>(() => {
    const out: FlatRow[] = [];

    for (const lot of inv.data?.rows ?? []) {
      out.push({
        key:          `INV-${lot.id}`,
        src:          "INV",
        catalog:      lot.catalogNo,
        supplier:     lot.supplier.name,
        size:         lot.sizeLabel,
        price:        lot.unitPrice,
        currency:     lot.currency,
        pricePerGram: null,                       // INV rows aren't normalized
        updated:      lot.receivedAt ?? "",
        isCheapest:   false,
      });
    }

    // Per-currency cheapest computation over latest-for-sku offers.
    const minByCurrency = new Map<string, number>();
    for (const g of mp.data?.groups ?? []) {
      for (const o of g.offers) {
        if (!o.isLatestForSku || o.pricePerGram == null) continue;
        const prev = minByCurrency.get(o.currency);
        if (prev === undefined || o.pricePerGram < prev) minByCurrency.set(o.currency, o.pricePerGram);
      }
    }

    for (const g of mp.data?.groups ?? []) {
      for (const o of g.offers) {
        const isCheapest =
          o.isLatestForSku &&
          o.pricePerGram !== null &&
          o.pricePerGram === minByCurrency.get(o.currency);
        out.push({
          key:          `SPP-${o.id}`,
          src:          "SPP",
          catalog:      o.productNo ?? "—",
          supplier:     g.supplier.name,
          scope:        g.scope,
          size:         o.packagingLabel ?? "—",
          price:        o.price,
          currency:     o.currency,
          pricePerGram: o.pricePerGram,
          updated:      o.priceSetAt.slice(0, 10),
          isCheapest,
        });
      }
    }
    return out;
  }, [inv.data, mp.data]);

  const shellHeader = {
    open:      true as const,
    onClose,
    eyebrow:   "Supplier catalog",
    title:     sc?.name ?? "—",
    accentVar: "var(--ink)",
  };

  if (mp.isPending && inv.isPending) {
    return (
      <ModalShell {...shellHeader} subtitle="">
        <ModalLoadingSkeleton kind="supplier" />
      </ModalShell>
    );
  }
  if (mp.isError || inv.isError) {
    return (
      <ModalShell {...shellHeader} subtitle="">
        <ModalErrorPanel
          onRetry={() => {
            qc.invalidateQueries({ queryKey: ["substanceMarketProducts"] });
            qc.invalidateQueries({ queryKey: ["substanceInventory"] });
          }}
        />
      </ModalShell>
    );
  }

  const subtitle = `${sc?.cas ?? "—"} · ${
    mp.data?.groups.length ?? 0
  } suppliers · ${inv.data?.rows.length ?? 0} stocked`;

  return (
    <ModalShell {...shellHeader} subtitle={subtitle}>
      <table className="data-table">
        <thead>
          <tr>
            <th style={{ width: 50 }}>Src</th>
            <th>Product No.</th>
            <th>Supplier</th>
            <th>Package size</th>
            <th style={{ textAlign: "right" }}>Price</th>
            <th style={{ textAlign: "right" }}>Per gram</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.key}
              style={r.isCheapest ? { background: "rgba(214,167,77,0.08)" } : undefined}
            >
              <td>
                <span
                  className={cn(
                    "supplier-row-src",
                    r.src === "INV" ? "inv" : "spp",
                  )}
                >
                  {r.src}
                </span>
              </td>
              <td className="mono">{r.catalog}</td>
              <td>
                {r.supplier}
                {scopeChipLabel(r.scope) && (
                  <>
                    {" "}
                    <span
                      className="mono"
                      style={{ fontSize: 10, color: "var(--ink-4)" }}
                    >
                      · {scopeChipLabel(r.scope)}
                    </span>
                  </>
                )}
              </td>
              <td className="mono">{r.size}</td>
              <td className="mono" style={{ textAlign: "right" }}>
                {r.price.toLocaleString("en-US")} {r.currency}
              </td>
              <td
                className="mono"
                style={{
                  textAlign: "right",
                  fontWeight: r.isCheapest ? 600 : 400,
                  color: r.isCheapest ? "var(--brand-green)" : "var(--ink)",
                }}
              >
                {r.pricePerGram != null
                  ? `${r.pricePerGram.toFixed(2)} ${r.currency}/g`
                  : "—"}
              </td>
              <td className="mono" style={{ color: "var(--ink-3)" }}>
                {r.updated}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={7}
                style={{
                  padding: 28,
                  textAlign: "center",
                  color: "var(--ink-4)",
                }}
              >
                No suppliers registered for this substance.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </ModalShell>
  );
}
